/**
 * useAppControl - Hook for fetching app control settings from database
 * 
 * Used for:
 * - Maintenance mode (redirects all users to /maintenance)
 * - Login disabled (blocks login attempts)
 * - Free World toggle (disables Free World access)
 * - Global banner (shows info/warning messages)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AppControlSettings {
  id: string;
  singleton_key: string;
  maintenance_enabled: boolean;
  maintenance_message: string;
  maintenance_severity: 'info' | 'warning' | 'outage';
  login_disabled: boolean;
  freeworld_enabled: boolean;
  banner_enabled: boolean;
  banner_message: string;
  banner_severity: 'info' | 'warning';
  freeworld_locations_locked: boolean;
  updated_at: string;
  updated_by: string | null;
}

const APP_CONTROL_QUERY_KEY = ['app_control'];

// Fetch the singleton app_control row
async function fetchAppControl(): Promise<AppControlSettings | null> {
  const { data, error } = await supabase
    .from('app_control')
    .select('*')
    .eq('singleton_key', 'singleton')
    .single();

  if (error) {
    console.error('[useAppControl] Error fetching app_control:', error);
    // Return safe defaults if fetch fails - don't block the app
    return null;
  }

  return data as AppControlSettings;
}

// Update app_control settings (admin only)
async function updateAppControl(settings: Partial<AppControlSettings>): Promise<AppControlSettings> {
  const { data, error } = await supabase
    .from('app_control')
    .update(settings)
    .eq('singleton_key', 'singleton')
    .select()
    .single();

  if (error) {
    console.error('[useAppControl] Error updating app_control:', error);
    throw error;
  }

  return data as AppControlSettings;
}

/**
 * Hook to read app control settings
 * Cached for 60 seconds to avoid excessive requests
 */
export function useAppControl() {
  return useQuery({
    queryKey: APP_CONTROL_QUERY_KEY,
    queryFn: fetchAppControl,
    staleTime: 60 * 1000, // 60 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    retry: 2,
  });
}

/**
 * Hook to update app control settings (admin only)
 */
export function useUpdateAppControl() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAppControl,
    onSuccess: (data) => {
      // Update the cache with new data
      queryClient.setQueryData(APP_CONTROL_QUERY_KEY, data);
    },
    onError: (error) => {
      console.error('[useUpdateAppControl] Mutation error:', error);
    },
  });
}

/**
 * Helper function to get default/safe settings when data is unavailable
 */
export function getDefaultAppControl(): AppControlSettings {
  return {
    id: '',
    singleton_key: 'singleton',
    maintenance_enabled: false,
    maintenance_message: '',
    maintenance_severity: 'outage',
    login_disabled: false,
    freeworld_enabled: true,
    banner_enabled: false,
    banner_message: '',
    banner_severity: 'info',
    freeworld_locations_locked: true,
    updated_at: new Date().toISOString(),
    updated_by: null,
  };
}
